"use client"

import { useState } from "react"

import { ArrowRight, Info } from "lucide-react"

import { Button } from "@/components/ui/Button"

import OrderController from "../../app/controllers/order/order.controller"
import { SubscriptionTiers } from "../../app/model/domain/subscription"
import underlineIcon from "../../assets/images/order/underline.png"
import { FeatureSectionTitle } from "./components/FeatureSectionTitle"
import { FeaturesTable } from "./components/FeaturesTable"
import { Header } from "./components/Header"
import { PricingCard } from "./components/PricingCard"

import { ContactDialog } from "./components/contact-partnership-dialog-components/ContactDialog"
import { ConfirmDialog } from "./components/contact-partnership-dialog-components/ConfirmDialog"
import { ErrorDialog } from "./components/contact-partnership-dialog-components/ErrorDialog"
import { LoadingDialog } from "./components/contact-partnership-dialog-components/LoadingDialog"

// ==================== Types ====================

export interface PricingPageProps {
  controller: OrderController
}

type PricingTab = "teacher" | "school"

// ==================== Constants ====================

function getTeacherFeatures(controller: OrderController) {
  return [
    {
      name: "Assignment Slots/Teacher",
      values: [
        "10",
        ...SubscriptionTiers.map((tier) => tier.assignmentSlots.toString()),
      ],
    },
    {
      name: "Student Pages/Year",
      values: [
        "Limited",
        ...SubscriptionTiers.map((tier) => tier.studentPages.toLocaleString()),
      ],
      link: () => controller.goToStudentWorkPage(),
      infoTooltip: (
        <span>
          Represents the total volume of student work during your subscription
          period. See examples{" "}
          <Button
            className="tw-pl-0 tw-text-white tw-text-xs tw-underline"
            variant="link"
            onClick={() => controller.goToStudentWorkPage()}
          >
            <i>here</i>
          </Button>
        </span>
      ),
    },
    {
      name: "Grades Synced",
      values: [
        "❌",
        ...SubscriptionTiers.map((tier) => tier.gradesSynced.toString()),
      ],
    },
    {
      name: "Professional Development (PD) Sessions",
      values: ["❌", ...SubscriptionTiers.map(() => "3")],
    },
    {
      name: "Student Portfolio Accounts",
      values: ["❌", ...SubscriptionTiers.map(() => "✔️")],
    },
    {
      name: "STEM Fill in the Blank",
      values: ["❌", ...SubscriptionTiers.map(() => "✔️")],
    },
    {
      name: "Google Classroom Integration",
      values: ["❌", ...SubscriptionTiers.map(() => "✔️")],
    },
    {
      name: "Export Student Grades and Answers",
      values: ["❌", ...SubscriptionTiers.map(() => "✔️")],
    },
    {
      name: "Export All Student Work to PDF",
      values: ["❌", ...SubscriptionTiers.map(() => "✔️")],
    },
    {
      name: "Organizational Assignment Library",
      values: ["❌", ...SubscriptionTiers.map(() => "❌")],
    },
  ]
}

function getSchoolFeatures(controller: OrderController) {
  return [
    {
      name: "Number of Teacher Licenses",
      values: [
        "Between 2-6 Teachers",
        "Between 7-12 Teachers",
        "Between 30-50 Teachers",
      ],
    },
    {
      name: "Grades synced",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      name: "Student Pages/Year",
      values: ["Unlimited", "Unlimited", "Unlimited"],
      link: () => controller.goToStudentWorkPage(),
      infoTooltip: (
        <span>
          Represents the total volume of student work during your subscription
          period. See examples{" "}
          <Button
            className="tw-pl-0 tw-text-white tw-text-xs tw-underline"
            variant="link"
            onClick={() => controller.goToStudentWorkPage()}
          >
            <i>here</i>
          </Button>
        </span>
      ),
    },
    {
      name: "Assignment Slots/Teacher",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      name: "Professional Development (PD) Sessions",
      values: ["4", "6", "8"],
    },
    {
      name: "Student Portfolio Accounts",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      name: "School Assignment Library",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "STEM Fill in the Blank",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "Export Student Grades and Answers",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "Export All Student Work to PDF",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "Google Classroom Integration",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "Team/School/District Assignment Library",
      values: ["✔️", "✔️", "✔️"],
    },
    {
      name: "Clever and Classlink Rostering",
      values: ["❌", "❌", "✔️"],
    },
  ]
}

// ==================== Component ====================

export function PricingPage({ controller }: PricingPageProps) {
  const [pricingTab, setPricingTab] = useState<PricingTab>(
    controller.viewingTeacherTab ? "teacher" : "school",
  )

  const [showContactDialog, setShowContactDialog] = useState(false)
  const [contactPlanType, setContactPlanType] = useState("")
  const [contactPrefilledEmail, setContactPrefilledEmail] = useState<
    string | undefined
  >()

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState("")

  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const [showLoadingDialog, setShowLoadingDialog] = useState(false)

  // ==================== Handlers ====================

  async function handleShowInterest(planType: string) {
    setShowLoadingDialog(true)

    try {
      let prefilledEmail: string | undefined

      if (controller.isLoggedIn) {
        const user = await controller._cacheService.getUser(true)
        prefilledEmail = user.email
      }

      setShowLoadingDialog(false)
      setContactPlanType(planType)
      setContactPrefilledEmail(prefilledEmail)
      setShowContactDialog(true)
    } catch (error) {
      setShowLoadingDialog(false)
      setErrorMessage(
        "Please try again and if you continue to have issues send us an email at support@classkick.com",
      )
      setShowErrorDialog(true)
    }
  }

  async function handleContactSubmit(
    fromAddress: string,
    subject: string,
    body: string,
  ) {
    await controller._orderService.showSchoolPlanInterest(
      body,
      fromAddress,
      contactPlanType,
    )
  }

  function handleContactSuccess() {
    setShowContactDialog(false)
    setConfirmMessage(
      "Thank you for your message, we will be in touch shortly!",
    )
    setShowConfirmDialog(true)
  }

  function handleContactError() {
    setShowContactDialog(false)
    setErrorMessage(
      "Please try again and if you continue to have issues send us an email at support@classkick.com",
    )
    setShowErrorDialog(true)
  }

  // ==================== Render Helpers ====================

  function renderTeacherBasicCard() {
    return (
      <PricingCard
        title="Basic"
        price="$ 0"
        description="I'm trying out practice and feedback for students"
        features={[
          "10 Total Assignment Slots",
          <span key="student-pages">
            Limited Student Pages Per Month
            <Button
              className="tw-pl-1 tw-text-gray-900 tw-text-xs tw-underline"
              variant="link"
              onClick={() => controller.goToStudentWorkPageNewTab()}
            >
              <Info size={6} />
            </Button>
          </span>,
        ]}
        buttonText="Sign up for free"
        buttonVariant="outline"
        onButtonClick={() => controller.goToSignup()}
      />
    )
  }

  function renderTeacherPlanCards() {
    return (
      <>
        {SubscriptionTiers.map((tier) => (
          <PricingCard
            key={tier.id}
            title={tier.label}
            price={<span>$ {tier.price}</span>}
            period="/mo"
            description={`Perfect for teachers who want more features and ${tier.assignmentSlots} assignment slots`}
            features={[
              `${tier.assignmentSlots} Total Assignment Slots`,
              <span key="student-pages">
                {tier.studentPages.toLocaleString()} Student Pages/Year
                <Button
                  className="tw-pl-1 tw-text-gray-900 tw-text-xs tw-underline"
                  variant="link"
                  onClick={() => controller.goToStudentWorkPageNewTab()}
                >
                  <Info size={6} />
                </Button>
              </span>,
              `${tier.gradesSynced} Grades Synced`,
              "Portfolio Student Accounts",
            ]}
            buttonText="Purchase (Monthly Only)"
            onButtonClick={() => controller.goToProTeacherPayment(tier.id)}
          />
        ))}
      </>
    )
  }

  function renderSchoolPlanCards() {
    return (
      <>
        <PricingCard
          title="Team"
          price="$ 749 - $ 1,699"
          period="/year"
          description="Perfect for departments, eg: Math, ELA, Science needing 2-6 teacher licenses"
          features={[
            "Between 2-6 Teacher Licenses",
            "Unlimited Assignment Slots and Student Pages",
            "Included Professional Development (PD) Sessions",
            "Team Collaboration",
          ]}
          buttonText="We are Interested"
          onButtonClick={() => handleShowInterest("Team")}
        />

        <PricingCard
          title="Pro School"
          price="$ 2,399 - $ 3,199"
          period=" /year"
          description="We are ready for 7-12 teachers in our school to drive mastery and learning outcomes"
          features={[
            "Between 7-12 Teacher Licenses",
            "Unlimited Assignment Slots and Student Pages",
            "Included Professional Development (PD) Sessions",
            "School Wide Collaboration",
          ]}
          buttonText="We are Interested"
          onButtonClick={() => handleShowInterest("School")}
        />

        <PricingCard
          title="Pro District"
          price="Starts at $ 9,999"
          period=" /year"
          description="We're ready to monitor data, drive mastery and learning outcomes, and increase curriculum fidelity by 10x"
          features={[
            "Between 30-50 Teacher Licenses",
            "Unlimited Assignment Slots and Student Pages",
            "Included Professional Development (PD) Sessions",
            "District Curriculum Sharing",
            "Google and Clever Rostering",
          ]}
          buttonText="We are Interested"
          onButtonClick={() => handleShowInterest("District")}
        />
      </>
    )
  }

  function renderToggle() {
    return (
      <div className="tw-relative tw-w-full sm:tw-max-w-[280px] tw-max-w-[200px] tw-h-[36px] sm:tw-h-[40px] tw-mt-15 tw-mb-8 tw-border-gray-200 tw-border-2 tw-flex tw-bg-white tw-rounded-full tw-mx-auto tw-p-1 tw-overflow-hidden">
        <div
          className={`tw-absolute tw-top-1 tw-bottom-1 tw-w-[calc(50%-2px)] tw-bg-green-100 tw-rounded-full tw-transition-transform tw-duration-300 tw-ease-in-out ${
            pricingTab === "teacher"
              ? "tw-translate-x-0"
              : "tw-translate-x-[calc(100%+4px)]"
          }`}
          aria-hidden="true"
        />

        <div className="tw-relative tw-z-10 tw-flex tw-w-full">
          <button
            className={`toggle-btn tw-flex-1 tw-px-2 sm:tw-px-4 tw-py-1.5 sm:tw-py-2 tw-text-xs sm:tw-text-base tw-whitespace-nowrap tw-transition-colors tw-duration-300 ${
              pricingTab === "teacher"
                ? "tw-text-green-100 active"
                : "tw-text-gray-500 tw-font-black"
            }`}
            onClick={() => setPricingTab("teacher")}
          >
            Teachers
          </button>

          <button
            className={`toggle-btn tw-flex-1 tw-px-2 sm:tw-px-4 tw-py-1.5 sm:tw-py-2 tw-text-xs sm:tw-text-base tw-whitespace-nowrap tw-transition-colors tw-duration-300 ${
              pricingTab === "school"
                ? "tw-text-green-900 active"
                : "tw-text-gray-500 tw-font-black"
            }`}
            onClick={() => setPricingTab("school")}
          >
            Schools / Districts
          </button>
        </div>
      </div>
    )
  }

  function renderViewMorePlansButton() {
    return (
      <div className="tw-w-full tw-flex tw-justify-center tw-mt-4">
        <Button
          className="tw-pl-0 tw-text-primary tw-font-semibold tw-text-base tw-underline tw-flex tw-items-center tw-gap-1"
          variant="link"
          onClick={() =>
            setPricingTab(pricingTab === "teacher" ? "school" : "teacher")
          }
        >
          View more plans <ArrowRight size={14} />
        </Button>
      </div>
    )
  }

  function renderTeacherFeaturesTable() {
    return (
      <div className="tw-max-w-screen-xl tw-mx-auto tw-my-16 tw-px-5">
        <FeatureSectionTitle withRays={true} title="Compare All Features" />

        <FeaturesTable
          columns={[
            {
              title: "Basic",
              description:
                "I'm trying out more practice and feedback for students",
            },
            ...SubscriptionTiers.map((tier) => ({
              title: tier.label,
              description: `Perfect for teachers who want ${tier.assignmentSlots} assignment slots and advanced features`,
            })),
          ]}
          features={getTeacherFeatures(controller)}
          ctaButtons={[
            {
              text: "Sign up for free",
              variant: "outline",
              onClick: () => controller.goToSignup(),
            },
            ...SubscriptionTiers.map((tier) => ({
              text: "Purchase (Monthly Only)",
              variant: "default" as const,
              onClick: () => controller.goToProTeacherPayment(tier.id),
            })),
          ]}
        />
      </div>
    )
  }

  function renderSchoolFeaturesTable() {
    return (
      <div className="tw-max-w-screen-xl tw-mx-auto tw-my-20 tw-px-5">
        <FeatureSectionTitle withRays={true} title="Compare All Features" />

        <FeaturesTable
          columns={[
            {
              title: "Team",
              description: "Perfect for departments, eg: Math, ELA, Science",
            },
            {
              title: "Pro School",
              description:
                "We are ready for the teachers in our school to drive mastery and learning outcomes",
            },
            {
              title: "Pro District",
              description:
                "We are ready for all the schools in our district to drive mastery and learning outcomes",
            },
          ]}
          features={getSchoolFeatures(controller)}
          ctaButtons={[
            {
              text: "We are Interested",
              variant: "default",
              onClick: () => handleShowInterest("Team"),
            },
            {
              text: "We are Interested",
              variant: "default",
              onClick: () => handleShowInterest("School"),
            },
            {
              text: "We are Interested",
              variant: "default",
              onClick: () => handleShowInterest("District"),
            },
          ]}
        />
      </div>
    )
  }

  // ==================== Main Render ====================

  // Legacy Refactor: The height calculation on the topmost element accounts for:
  //   - Zoom of 1.6 on .react-container in src/index.css
  return (
    <div
      className="tw-overflow-auto tw-font-sansAlt"
      style={{ height: `${window.innerHeight / 1.6}px` }}
    >
      <Header
        isLoggedIn={controller.isLoggedIn}
        goToHomepage={() => controller.goToHomepage()}
        goToTeacherHelpCenter={() => controller.goToTeacherHelpCenter()}
        goToPlan={() => controller.goToPlan()}
        goToLogin={() => controller.goToLogin()}
        goToProfile={() => controller.goToProfile()}
      />

      <div>
        <div className="tw-max-w-screen-xl tw-mx-auto tw-my-16 tw-px-5">
          <div className="tw-font-extrabold tw-text-center tw-mb-8 tw-text-xl sm:tw-text-2xl md:tw-text-[24px] tw-text-black tw-relative tw-px-4">
            Options for all classrooms to
            <span className="tw-relative tw-inline-block">
              &nbsp;help students grow
              <img
                className="tw-absolute tw-left-0 tw-bottom-[-12px] sm:tw-bottom-[-6px] md:tw-bottom-[-10px] tw-w-[180px] sm:tw-w-[220px] md:tw-w-[250px]"
                src={underlineIcon}
                alt="Underline"
              />
            </span>
          </div>

          {renderToggle()}

          {pricingTab === "teacher" ? (
            <div className="tw-flex tw-flex-wrap tw-gap-5 tw-justify-center tw-mb-10">
              {renderTeacherBasicCard()}
              {renderTeacherPlanCards()}
              {renderViewMorePlansButton()}
            </div>
          ) : (
            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-justify-center tw-mb-10">
              {renderSchoolPlanCards()}
              <div className="tw-w-full tw-flex tw-justify-center">
                <span className="tw-text-base tw-font-medium tw-text-gray-900 tw-m-2">
                  Need a customized quote or have questions?
                </span>
                <Button
                  className="tw-pl-0 tw-text-primary tw-font-medium tw-text-base tw-flex tw-items-center tw-gap-1 tw-m-1"
                  variant="link"
                  onClick={() => handleShowInterest("School")}
                >
                  Click here to contact us
                </Button>
              </div>
            </div>
          )}
        </div>

        {pricingTab === "teacher"
          ? renderTeacherFeaturesTable()
          : renderSchoolFeaturesTable()}

        {renderViewMorePlansButton()}

        <div className="tw-text-center tw-my-8 tw-mb-24 tw-px-2.5">
          <span className="tw-text-black tw-font-black tw-text-base">
            Questions?
          </span>
          <span className="tw-text-light tw-text-base">
            &nbsp;Check out our
          </span>
          <Button
            className="tw-pl-1 tw-pr-0 tw-font-bold tw-text-base"
            variant="link"
            onClick={() => controller.openPricingFAQs()}
          >
            Pricing FAQs
          </Button>
          <span className="tw-text-light tw-text-base">
            &nbsp;or shoot us an email at pro@classkick.com
          </span>
        </div>
      </div>

      {showContactDialog && (
        <ContactDialog
          planType={contactPlanType}
          prefilledEmail={contactPrefilledEmail}
          onSubmit={handleContactSubmit}
          onSuccess={handleContactSuccess}
          onError={handleContactError}
          onClose={() => setShowContactDialog(false)}
        />
      )}

      {showConfirmDialog && (
        <ConfirmDialog
          open={showConfirmDialog}
          title="We are on it!"
          message={confirmMessage}
          onClose={() => setShowConfirmDialog(false)}
        />
      )}

      {showErrorDialog && (
        <ErrorDialog
          message={errorMessage}
          onClose={() => setShowErrorDialog(false)}
        />
      )}

      {showLoadingDialog && (
        <LoadingDialog
          message="Loading..."
          onClose={() => setShowLoadingDialog(false)}
        />
      )}
    </div>
  )
}
